import PlusIcon from "@assets/icons/plus.svg";
import { useI18n } from "@core/i18n/i18n";
import { libraryFilterManager } from "@core/service-container";
import { BaseExercise, Exercise, MainExercise } from "@domain/exercise/exercise";
import {
	AnalyticsViewExerciseDetailOrigin,
	sendAnalyticsEventAddAlternativeExercises,
	sendAnalyticsEventDeleteAlternativeExercises,
} from "@domain/library/exercise-analytics";
import { useExerciseFormatter } from "@ui/components/exercise/hooks/use-exercise-formatter";
 
import styled from "@emotion/styled";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
 
import { Tooltip } from "@ui/components/common/tooltip";
import { ResponsiveDeviceType } from "@ui/hooks/use-dimensions";
import { AlternativeExerciseCard } from "@ui/pages/program-builder/alternative-exercise/alternative-exercise-card";
import {
	AlternativeErrorType,
	MAX_ALTERNATIVE_EXERCISES,
} from "@ui/pages/program-builder/alternative-exercise/alternative-exercises-utils";
import { AlternativeError } from "@ui/pages/program-builder/alternative-exercise/components/alternative-error";
import { AlternativeExerciseHeader } from "@ui/pages/program-builder/alternative-exercise/components/alternative-exercise-header";
import { NoAlternativeExercisePlaceholder } from "@ui/pages/program-builder/alternative-exercise/components/no-alternative-exercise-placeholder";
import { useEditorExerciseCardActions } from "@ui/pages/program-builder/hooks/use-editor-exercise-card-actions";
import { ExerciseCard } from "@ui/pages/program-builder/library/exercise-card";
import { ExerciseLibraryFlow } from "@ui/pages/program-builder/mobile/dialog/mobile-exercise-library-dialog";
 
import { row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { reorder } from "@utils/array-utils";
import { isNotNil } from "@utils/assert";
import { Dialog, DialogProps } from "primereact/dialog";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { createBodyPartsFilter, createCategoriesFilter } from "@domain/exercise/exercise-filters";

export interface MobileAlternativeExerciseDialogProps extends DialogProps {
	exercise: MainExercise;
	onSave?: (newExercise: MainExercise) => void;
}

export const MobileAlternativeExerciseDialog: React.FC<MobileAlternativeExerciseDialogProps> = props => {
	const { exercise, onSave, onHide, ...otherProps } = props;
	const { formatExerciseBodyPart, formatExerciseCategory } = useExerciseFormatter();

	const { format } = useI18n();
	const dialogContext = useContext(DialogContext);
 

	const [error, setError] = useState<AlternativeErrorType | null>(null);
	const [alternatives, setAlternatives] = useState<BaseExercise[]>(exercise.alternativeExercises);

	const mainAndAlternativeExercises = [exercise, ...alternatives];

	const addAlternatives = useCallback(
		(exercises: BaseExercise[]) => {
			const newAlternatives = exercises.reduce((list, newAlternative) => {
				if (list.length === MAX_ALTERNATIVE_EXERCISES) {
					setError(AlternativeErrorType.MaxExercisesReached);
					return list;
				}
				if (list.some(ex => ex.id === newAlternative.id) || exercise.id === newAlternative.id) {
					setError(AlternativeErrorType.OneAlreadyExists);
					return list;
				}
				sendAnalyticsEventAddAlternativeExercises(exercise, newAlternative);
				return [
					...list,
					{
						...newAlternative,
						alternatives: null,
					},
				];
			}, alternatives);

			if (exercises.length > 0) {
				libraryFilterManager.setLastAddedExerciseMetadata(exercises[0].draggableId, true);
			}
			setAlternatives(newAlternatives);
		},
		[alternatives, exercise],
	);

	const reorderAlternatives = useCallback(
		(sourceIndex: number, destinationIndex: number) => {
			setAlternatives(reorder(alternatives, sourceIndex, destinationIndex));
		},
		[alternatives],
	);

	const deleteAlternative = useCallback(
		(deletedAlternative: BaseExercise) => {
			sendAnalyticsEventDeleteAlternativeExercises(exercise, deletedAlternative);
			setAlternatives(alternatives.filter(alternative => alternative.draggableId !== deletedAlternative.draggableId));
		},
		[alternatives, exercise],
	);

	const onCrossClicked = useCallback(async () => {
		onSave?.({ ...exercise, alternativeExercises: alternatives });
		onHide();
	libraryFilterManager.clearActiveFilteringState();
	}, [alternatives, exercise, onHide, onSave]);

	const openLibrary = useCallback(async () => {
		const filteredBodyParts = [exercise.primaryBodyPart, ...exercise.secondaryBodyParts];
		libraryFilterManager.exerciseWithAlternativesEdited.set({ ...exercise, alternativeExercises: alternatives });
		libraryFilterManager.activeCategoryFilters.set([
			createBodyPartsFilter(filteredBodyParts, format("exercise.bodyPart.title"), formatExerciseBodyPart),
			createCategoriesFilter([exercise.category], format("exercise.category.title"), formatExerciseCategory),
		]);
		libraryFilterManager.setIsAlternativeExerciseActive();
		dialogContext?.openModal(ModalId.MobileExerciseLibrary, {
			exerciseLibraryAddingFlow: ExerciseLibraryFlow.AlternativeExercises,
			onClose: exercises => { void (async () => { addAlternatives(exercises); })(); },
		});
	}, [addAlternatives, alternatives, dialogContext, exercise, format, formatExerciseBodyPart, formatExerciseCategory]);

	const updateAlternative = useCallback(
		(updatedAlternative: Exercise) => {
			setAlternatives(
				alternatives.map(alternative =>
					alternative.draggableId === updatedAlternative.draggableId
						? {
								...updatedAlternative,
								alternatives: null,
							}
						: alternative,
				),
			);
		},
		[alternatives],
	);

	const { openExerciseDetailsModal } = useEditorExerciseCardActions();

	const maxAlternativesNotReached = useMemo(
		() => isNotNil(alternatives) && alternatives.length < MAX_ALTERNATIVE_EXERCISES,
		[alternatives],
	);

	return (
		<StyledDialog
			draggable={false}
			resizable={false}
			onHide={onHide}
			blockScroll={true}
			{...otherProps}
			header={<AlternativeExerciseHeader onCrossClicked={onCrossClicked} />}
			footer={
				<Footer>
					<Tooltip
						content={format("programBuilder.alternativeExercises.errors.maximumReached")}
						disabled={maxAlternativesNotReached}
					>
						<PlusContainer
							onClick={maxAlternativesNotReached ? openLibrary : undefined}
							clickable={maxAlternativesNotReached}
						>
							<PlusIcon width={24} height={24} color={colors.black} />
						</PlusContainer>
					</Tooltip>
					<p className="p-size-s-bold">{format("programBuilder.alternativeExerciseDialog.addAlternativesButton")}</p>
				</Footer>
			}
		>
			<Body>
				<AlternativeError errorType={error} cleanError={() => setError(null)} />
                
				<ExerciseCard
					exercise={exercise}
					deviceType={ResponsiveDeviceType.Mobile}
					showAlternatives={false}
					onlyAllowPreview
					hideContent
					onClick={() =>
						openExerciseDetailsModal(exercise, mainAndAlternativeExercises, AnalyticsViewExerciseDetailOrigin.CANVAS)
					}
				/>
				<Separator />
				<AlternativeContainer>
					{isNotNil(alternatives) &&
						alternatives.map((alternative, alternativeIndex) => (
							<AlternativeExerciseCard
								key={alternative.draggableId}
								alternatives={alternatives}
								alternativeExercise={alternative}
								alternativeExerciseIndex={alternativeIndex}
								relatedExercises={mainAndAlternativeExercises}
								onReorder={reorderAlternatives}
								onDelete={deleteAlternative}
								deviceType={ResponsiveDeviceType.Mobile}
								updateExercise={updateAlternative}
							/>
						))}

					{maxAlternativesNotReached && <NoAlternativeExercisePlaceholder onClick={openLibrary} />}
				</AlternativeContainer>
                
			</Body>
		</StyledDialog>
	);
};

const StyledDialog = styled(Dialog)`
	max-width: 450px;
	max-height: 1000px;
	width: 100%;
	height: 95%;
	margin: 16px;

	.p-dialog-content {
		background-color: ${colors.primary10};
		border-radius: 0;
		padding: 0;
	}

	.p-dialog-header {
		background-color: ${colors.primary10};
		overflow: hidden;
		padding: 16px;

		.p-dialog-header-icons {
			display: none;
		}
	}

	.p-dialog-footer {
		background-color: ${colors.primary10};
		overflow: hidden;
		padding: 0;
	}
`;

const Body = styled.div`
	padding: 0 12px;
`;

const AlternativeContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	flex: 1;
	margin-bottom: 8px;
`;

const Footer = styled.div`
	padding: 16px;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 8px;
	background-color: ${colors.primaryBackground};
	border-top: 1px solid ${colors.primary25};
`;

const PlusContainer = styled.div<{ clickable: boolean }>`
	height: 32px;
	width: 32px;
	${row(0, "center", "center")}
	border-radius: 100%;
	background-color: ${({ clickable }) => (clickable ? colors.primary25 : colors.grey25)};
	cursor: ${({ clickable }) => (clickable ? "pointer" : "not-allowed")};
`;

const Separator = styled.div`
	margin: 12px 0;
	height: 2px;
	background-color: ${colors.primary25};
`;

 
